import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTemplate } from '@/contexts/TemplateContext'
import type { PublicProfile } from '@/types'
import { Gallery } from '@/components/gallery'
import { BookingModal } from '@/components/booking/BookingModal'

interface PhotographerTemplateProps {
    profile: PublicProfile
}

export function PhotographerTemplate({ profile }: PhotographerTemplateProps) {
    const { t } = useTranslation('public')
    const { colors, typography } = useTemplate()
    const [isBookingOpen, setIsBookingOpen] = useState(false)

    return (
        <div
            className="min-h-screen"
            style={{
                fontFamily: typography.body,
                backgroundColor: colors.colors.background,
                color: colors.colors.text,
            }}
        >
            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center">
                <div className="absolute inset-0">
                    {profile.cover_image ? (
                        <img
                            src={profile.cover_image}
                            alt=""
                            className="w-full h-full object-cover"
                        />
                    ) : profile.profile_image ? (
                        <img
                            src={profile.profile_image}
                            alt=""
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full bg-gradient-to-br from-gray-900 via-gray-800 to-black" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
                </div>
                <div className="relative z-10 max-w-7xl mx-auto px-4 py-20 md:px-6 md:py-24 lg:px-8 min-h-screen flex items-center">
                    <div className="max-w-2xl">
                        {profile.tagline && (
                            <p
                                className="uppercase tracking-widest mb-4 text-sm"
                                style={{ color: colors.colors.accent }}
                            >
                                {profile.tagline}
                            </p>
                        )}
                        <h1
                            className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6"
                            style={{ fontFamily: typography.heading }}
                        >
                            {t('template.photographer.capturingMomentsThatLastForever')}
                        </h1>
                        <p className="text-xl text-white/80 mb-8 leading-relaxed">
                            {profile.bio || t('template.photographer.creatingTimelessImagery')}
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <a
                                href="#portfolio"
                                className="inline-flex items-center px-8 py-4 rounded-full font-medium transition-all hover:opacity-90"
                                style={{ backgroundColor: colors.colors.accent, color: colors.colors.primary }}
                            >
                                {t('template.photographer.viewPortfolio')}
                            </a>
                            <a
                                href="#contact"
                                className="inline-flex items-center px-8 py-4 border-2 border-white text-white rounded-full font-medium hover:bg-white hover:text-black transition-all"
                            >
                                {t('template.photographer.bookSession')}
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section id="about" className="py-16 md:py-24 px-4 md:px-6 lg:px-8 bg-white">
                <div className="max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                        <div className="order-2 lg:order-1">
                            <p
                                className="uppercase tracking-widest mb-4 text-sm"
                                style={{ color: colors.colors.accent }}
                            >
                                About
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.photographer.about')}
                            </h2>
                            {profile.bio && (
                                <p className="text-lg mb-6 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                    {profile.bio}
                                </p>
                            )}
                            {profile.location && (
                                <p className="text-sm" style={{ color: colors.colors.textMuted }}>
                                    📍 {profile.location}
                                </p>
                            )}
                        </div>
                        <div className="order-1 lg:order-2 relative">
                            {profile.profile_image ? (
                                <img
                                    src={profile.profile_image}
                                    alt={profile.business_name}
                                    className="rounded-2xl shadow-2xl w-full object-cover"
                                />
                            ) : (
                                <div
                                    className="rounded-2xl shadow-2xl w-full aspect-square flex items-center justify-center"
                                    style={{ backgroundColor: colors.colors.secondary }}
                                >
                                    <span
                                        className="text-6xl font-bold"
                                        style={{ color: colors.colors.primary }}
                                    >
                                        {profile.business_name[0]}
                                    </span>
                                </div>
                            )}
                            <div
                                className="absolute -bottom-6 -right-6 rounded-2xl p-6 shadow-xl"
                                style={{ backgroundColor: colors.colors.secondary }}
                            >
                                <span className="text-2xl" style={{ color: colors.colors.accent }}>📷</span>
                                <p className="font-semibold" style={{ color: colors.colors.text }}>{t('template.photographer.capturing')}</p>
                                <p className="text-sm" style={{ color: colors.colors.textMuted }}>{t('template.photographer.yourStory')}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Portfolio/Gallery Section */}
            {profile.gallery && profile.gallery.length > 0 && (
                <section id="portfolio" className="py-16 md:py-24 px-4 md:px-6 lg:px-8" style={{ backgroundColor: colors.colors.background }}>
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                            <p
                                className="uppercase tracking-widest mb-4 text-sm"
                                style={{ color: colors.colors.accent }}
                            >
                                {t('template.photographer.portfolio')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl font-bold"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.photographer.recentWork')}
                            </h2>
                        </div>
                        <Gallery
                            images={profile.gallery}
                            layout="grid"
                            limit={6}
                            enableLightbox
                            showCaptions
                            aspectRatio="portrait"
                            gapSize={4}
                        />
                    </div>
                </section>
            )}

            {/* Services/Packages Section */}
            {profile.services && profile.services.length > 0 && (
                <section id="services" className="py-16 md:py-24 px-4 md:px-6 lg:px-8 bg-white">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                            <p
                                className="uppercase tracking-widest mb-4 text-sm"
                                style={{ color: colors.colors.accent }}
                            >
                                {t('template.photographer.investment')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl font-bold"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.photographer.sessionPackages')}
                            </h2>
                        </div>
                        <div className="grid md:grid-cols-3 gap-8">
                            {profile.services.map((service, index) => (
                                <div
                                    key={service.id}
                                    className="rounded-2xl p-8 text-center transition-shadow hover:shadow-xl"
                                    style={{
                                        backgroundColor: colors.colors.background,
                                        border: index === 1 ? `2px solid ${colors.colors.accent}` : 'none',
                                    }}
                                >
                                    <div
                                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                                        style={{ backgroundColor: colors.colors.secondary }}
                                    >
                                        <span style={{ color: colors.colors.accent }}>
                                            {index === 0 ? '💕' : index === 1 ? '⭐' : '💎'}
                                        </span>
                                    </div>
                                    <h3
                                        className="text-xl font-bold mb-2"
                                        style={{ fontFamily: typography.heading, color: colors.colors.text }}
                                    >
                                        {service.name}
                                    </h3>
                                    {service.price_display !== 'hidden' && service.price && (
                                        <p
                                            className="text-3xl font-bold mb-4"
                                            style={{ color: colors.colors.accent }}
                                        >
                                            {service.price_display === 'from' && 'From '}${service.price.toFixed(0)}
                                            {service.price_display === 'contact' && 'Contact'}
                                        </p>
                                    )}
                                    {service.description && (
                                        <p className="mb-6 text-sm" style={{ color: colors.colors.textMuted }}>
                                            {service.description}
                                        </p>
                                    )}
                                    {service.duration_minutes && (
                                        <div className="flex items-center justify-center text-sm" style={{ color: colors.colors.textMuted }}>
                                            <span className="mr-2">⏱</span>
                                            <span>{service.duration_minutes} min</span>
                                        </div>
                                    )}
                                    <button
                                        onClick={() => setIsBookingOpen(true)}
                                        className="w-full py-3 mt-6 rounded-full font-medium text-center transition-colors hover:opacity-90"
                                        style={{
                                            backgroundColor: index === 1 ? colors.colors.accent : colors.colors.text,
                                            color: index === 1 ? colors.colors.primary : colors.colors.background,
                                        }}
                                    >
                                        {t('template.photographer.bookNow')}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Availability Section */}
            {profile.availability && profile.availability.length > 0 && (
                <section className="py-16 md:py-24 px-4 md:px-6 lg:px-8" style={{ backgroundColor: colors.colors.background }}>
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <p
                                className="uppercase tracking-widest mb-4 text-sm"
                                style={{ color: colors.colors.accent }}
                            >
                                {t('template.photographer.availability')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl font-bold"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.photographer.bookingInformation')}
                            </h2>
                        </div>
                        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
                            <div className="grid md:grid-cols-2 gap-8 mb-8">
                                {profile.availability.slice(0, 7).map((day) => (
                                    <div
                                        key={day.day_of_week}
                                        className="flex justify-between items-center py-3 border-b"
                                        style={{ borderColor: colors.colors.secondary }}
                                    >
                                        <span className="font-semibold" style={{ color: colors.colors.text }}>
                                            {day.day_name}
                                        </span>
                                        <span style={{ color: colors.colors.textMuted }}>
                                            {day.is_available
                                                ? day.is_all_day
                                                    ? t('template.common.allDay')
                                                    : `${day.start_time} - ${day.end_time}`
                                                : t('template.common.closed')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            {profile.location && (
                                <div className="text-center rounded-xl p-6" style={{ backgroundColor: colors.colors.secondary }}>
                                    <p className="text-sm" style={{ color: colors.colors.textMuted }}>
                                        <span className="mr-2" style={{ color: colors.colors.accent }}>ℹ️</span>
                                        {t('template.photographer.sessionsAvailableIn').replace('{{location}}', profile.location)}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            )}

            {/* Contact Section */}
            <section id="contact" className="py-16 md:py-24 px-4 md:px-6 lg:px-8 bg-white">
                <div className="max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
                        <div>
                            <p
                                className="uppercase tracking-widest mb-4 text-sm"
                                style={{ color: colors.colors.accent }}
                            >
                                {t('template.photographer.getInTouch')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.photographer.letsCreateTogether')}
                            </h2>
                            <p className="text-lg mb-8 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                {t('template.photographer.readyToBook')}
                            </p>
                            <div className="space-y-6">
                                {profile.email && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: colors.colors.secondary }}
                                        >
                                            <span style={{ color: colors.colors.accent }}>✉️</span>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold" style={{ color: colors.colors.text }}>{t('template.photographer.email')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.email}</p>
                                        </div>
                                    </div>
                                )}
                                {profile.phone && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: colors.colors.secondary }}
                                        >
                                            <span style={{ color: colors.colors.accent }}>📞</span>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold" style={{ color: colors.colors.text }}>{t('template.photographer.phone')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.phone}</p>
                                        </div>
                                    </div>
                                )}
                                {profile.location && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: colors.colors.secondary }}
                                        >
                                            <span style={{ color: colors.colors.accent }}>📍</span>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold" style={{ color: colors.colors.text }}>{t('template.photographer.location')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.location}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {profile.social_links && profile.social_links.length > 0 && (
                                <div className="mt-8">
                                    <h4 className="font-semibold mb-4" style={{ color: colors.colors.text }}>{t('template.photographer.followMyWork')}</h4>
                                    <div className="flex space-x-4">
                                        {profile.social_links.map((link) => (
                                            <a
                                                key={link.platform}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={`Follow on ${link.platform}`}
                                                className="w-12 h-12 rounded-xl flex items-center justify-center transition-all hover:opacity-80"
                                                style={{ backgroundColor: colors.colors.text, color: colors.colors.background }}
                                            >
                                                <span className="text-lg">
                                                    {link.platform === 'instagram' && '📸'}
                                                    {link.platform === 'facebook' && '📘'}
                                                    {link.platform === 'tiktok' && '🎵'}
                                                    {link.platform === 'pinterest' && '📌'}
                                                    {!['instagram', 'facebook', 'tiktok', 'pinterest'].includes(link.platform) && '🔗'}
                                                </span>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <form className="rounded-3xl p-8 shadow-lg" style={{ backgroundColor: colors.colors.background }}>
                                <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: typography.heading, color: colors.colors.text }}>
                                    {t('template.photographer.bookYourSession')}
                                </h3>
                                <div className="space-y-5">
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.text }}>
                                            {t('template.photographer.fullName')}
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-4 py-4 rounded-xl border outline-none transition-all"
                                            style={{
                                                backgroundColor: colors.colors.background,
                                                borderColor: colors.colors.secondary,
                                            }}
                                            placeholder={t('template.photographer.yourName')}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.text }}>
                                            {t('template.photographer.email')}
                                        </label>
                                        <input
                                            type="email"
                                            className="w-full px-4 py-4 rounded-xl border outline-none transition-all"
                                            style={{
                                                backgroundColor: colors.colors.background,
                                                borderColor: colors.colors.secondary,
                                            }}
                                            placeholder="your@email.com"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.text }}>
                                            {t('template.photographer.phone')}
                                        </label>
                                        <input
                                            type="tel"
                                            className="w-full px-4 py-4 rounded-xl border outline-none transition-all"
                                            style={{
                                                backgroundColor: colors.colors.background,
                                                borderColor: colors.colors.secondary,
                                            }}
                                            placeholder="(555) 123-4567"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.text }}>
                                            {t('template.photographer.sessionType')}
                                        </label>
                                        <select
                                            className="w-full px-4 py-4 rounded-xl border outline-none transition-all cursor-pointer"
                                            style={{
                                                backgroundColor: colors.colors.background,
                                                borderColor: colors.colors.secondary,
                                            }}
                                        >
                                            <option value="">{t('template.photographer.selectASessionType')}</option>
                                            {profile.services?.map((service) => (
                                                <option key={service.id} value={service.id}>
                                                    {service.name}
                                                    {service.price && ` - $${service.price.toFixed(0)}`}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <button
                                        type="submit"
                                        className="w-full py-4 rounded-xl font-medium text-center transition-colors hover:opacity-90"
                                        style={{ backgroundColor: colors.colors.text, color: colors.colors.background }}
                                    >
                                        {t('template.photographer.sendInquiry')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-4 md:px-6 lg:px-8 text-center" style={{ backgroundColor: colors.colors.text }}>
                <h3 className="text-2xl font-semibold mb-2" style={{ fontFamily: typography.heading, color: colors.colors.background }}>
                    {profile.business_name}
                </h3>
                {profile.social_links && profile.social_links.length > 0 && (
                    <div className="flex justify-center space-x-4 mb-6">
                        {profile.social_links.slice(0, 3).map((link) => (
                            <a
                                key={link.platform}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Follow on ${link.platform}`}
                                className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:opacity-80"
                                style={{ backgroundColor: colors.colors.background, color: colors.colors.text }}
                            >
                                <span className="text-sm">
                                    {link.platform === 'instagram' && '📸'}
                                    {link.platform === 'facebook' && '📘'}
                                    {link.platform === 'tiktok' && '🎵'}
                                    {!['instagram', 'facebook', 'tiktok'].includes(link.platform) && '🔗'}
                                </span>
                            </a>
                        ))}
                    </div>
                )}
                <p className="text-sm" style={{ color: colors.colors.textMuted }}>
                    © {new Date().getFullYear()} {profile.business_name}. {t('template.photographer.allRightsReserved')}
                </p>
            </footer>

            {isBookingOpen && profile.services && profile.services.length > 0 && (
                <BookingModal
                    isOpen={isBookingOpen}
                    onClose={() => setIsBookingOpen(false)}
                    profile={profile}
                    services={profile.services}
                    categories={[]}
                />
            )}
        </div>
    )
}

export default PhotographerTemplate;
